/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Color from 'color';
// material
import { ColorPicker, Icon, sendErrorMessage } from '@insticator/insticator-ui'; // iui-material
import ConfigController from 'material/expandablesection/controllers/configcontroller/configcontroller';
import ExpandableSection from 'material/expandablesection/expandablesection';
import SectionDescription from 'material/expandablesection/sectiondescription/sectiondescription';
import SectionCard from 'material/expandablesection/sectioncard/sectioncard';
import SectionGroup from 'material/expandablesection/sectiongroup/sectiongroup';
import SectionGroupDescription from 'material/expandablesection/sectiongroupdescription/sectiongroupdescription'
// utils
import { url } from 'utils/urls';
import { updateColorConfiguration } from 'state/actions/commentingState/configurationStatus';
// styles
import injectSheet from 'react-jss';
import styles from 'pages/dashboard/commenting/configuration/colorschemesection/colorschemesection_styles';
// svg assets
import { ReactComponent as ThumbDown } from '../imgassets/thumb-down-alt.svg'
import { ReactComponent as ThumbUp } from '../imgassets//thumb-up-alt.svg'
import { ReactComponent as ThreeDots } from '../imgassets/more-horiz.svg'
import { ReactComponent as Smile } from '../imgassets/mood.svg'
import { ReactComponent as Comment } from '../imgassets/author-comments-count.svg'

const ColorSchemeSection = (props) => {
  // Grab initial saved color configuration from redux state
  const savedColorConfiguration = useSelector(({ commentingState }) => commentingState.configStatus.colorSchema);
  const savingColorConfiguration = useSelector(({ commentingState }) => commentingState.configStatus.savingColorConfiguration);

  // Dynamically changing internal state
  const [backgroundcolor, setbackgroundcolor] = useState(savedColorConfiguration.background.value);
  const [primarytextcolor, setprimarytextcolor] = useState(savedColorConfiguration.primaryText.value);
  const [secondarytextcolor, setsecondarytextcolor] = useState(savedColorConfiguration.secondaryText.value);
  const [brandingcolor, setbrandingcolor] = useState(savedColorConfiguration.branding.value);
  const [primarybuttoncolor, setprimarybuttoncolor] = useState(savedColorConfiguration.primaryButtonLabel.value);
  const [errorvalidationcolor, seterrorvalidationcolor] = useState(savedColorConfiguration.errorValidation.value);
  // valid proper
  const [backgroundcolorvalid, setbackgroundcolorvalid] = useState(true);
  const [primarytextcolorvalid, setprimarytextcolorvalid] = useState(true);
  const [secondarytextcolorvalid, setsecondarytextcolorvalid] = useState(true);
  const [brandingcolorvalid, setbrandingcolorvalid] = useState(true);
  const [primarybuttoncolorvalid, setprimarybuttoncolorvalid] = useState(true);
  const [errorvalidationcolorvalid, seterrorvalidationcolorvalid] = useState(true);

  const [isOpened, setIsOpened] = useState(false);
  const dispatch = useDispatch();

  const processColorUpdate = (color, name) => {
    switch(name) {
      case 'Background':
        if (color === savedColorConfiguration.background.value) setbackgroundcolorvalid(true);
        setbackgroundcolor(color)
        return;
      case 'Primary Text':
        if (color === savedColorConfiguration.primaryText.value) setprimarytextcolorvalid(true);
        setprimarytextcolor(color)
        return;
      case 'Secondary Text':
        if (color === savedColorConfiguration.secondaryText.value) setsecondarytextcolorvalid(true);
        setsecondarytextcolor(color)
        return;
      case 'Branding':
        if (color === savedColorConfiguration.branding.value) setbrandingcolorvalid(true);
        setbrandingcolor(color)
        return;
      case 'Primary Button Labels':
        if (color === savedColorConfiguration.primaryButtonLabel.value) setprimarybuttoncolorvalid(true);
        setprimarybuttoncolor(color)
        return;
      case 'Error Validation':
        if (color === savedColorConfiguration.errorValidation.value) seterrorvalidationcolorvalid(true);
        seterrorvalidationcolor(color)
        return;
    }
  }

  const validateColor = (isValid, name) => {
    switch(name) {
      case 'Background':
        setbackgroundcolorvalid(isValid)
        return;
      case 'Primary Text':
        setprimarytextcolorvalid(isValid)
        return;
      case 'Secondary Text':
        setsecondarytextcolorvalid(isValid)
        return;
      case 'Branding':
        setbrandingcolorvalid(isValid)
        return;
      case 'Primary Button Labels':
        setprimarybuttoncolorvalid(isValid)
        return;
      case 'Error Validation':
        seterrorvalidationcolorvalid(isValid)
        return;
    }
  }

  const disableSave = backgroundcolor.toUpperCase() === savedColorConfiguration.background.value.toUpperCase()
    && primarytextcolor.toUpperCase() === savedColorConfiguration.primaryText.value.toUpperCase()
    && secondarytextcolor.toUpperCase() === savedColorConfiguration.secondaryText.value.toUpperCase()
    && brandingcolor.toUpperCase() === savedColorConfiguration.branding.value.toUpperCase()
    && primarybuttoncolor.toUpperCase() === savedColorConfiguration.primaryButtonLabel.value.toUpperCase()
    && errorvalidationcolor.toUpperCase() === savedColorConfiguration.errorValidation.value.toUpperCase();

  const handleCancel = () => {
    setbackgroundcolor(savedColorConfiguration.background.value);
    setbackgroundcolorvalid(true);
    setprimarytextcolor(savedColorConfiguration.primaryText.value);
    setprimarytextcolorvalid(true);
    setsecondarytextcolor(savedColorConfiguration.secondaryText.value);
    setsecondarytextcolorvalid(true);
    setbrandingcolor(savedColorConfiguration.branding.value);
    setbrandingcolorvalid(true);
    setprimarybuttoncolor(savedColorConfiguration.primaryButtonLabel.value);
    setprimarybuttoncolorvalid(true);
    seterrorvalidationcolor(savedColorConfiguration.errorValidation.value);
    seterrorvalidationcolorvalid(true);
  }

  const collapse = () => {
    setIsOpened(false);
  }

  const handleColorSchemeSave = () => {
    const customColorSchema = {
      uuid: savedColorConfiguration.uuid,
      background: backgroundcolor.substring(1),
      primaryText: primarytextcolor.substring(1),
      secondaryText: secondarytextcolor.substring(1),
      branding: brandingcolor.substring(1),
      primaryButtonLabel: primarybuttoncolor.substring(1),
      errorValidation: errorvalidationcolor.substring(1),
    };
    const validToSave = allColorsValid();

    if (!disableSave && validToSave) {
      dispatch(updateColorConfiguration(savedColorConfiguration.uuid, customColorSchema, collapse));
    } else if (!validToSave) {
      dispatch(sendErrorMessage('Cannot save: invalid colors'));
    }
  }

  const allColorsValid = () => {
    return backgroundcolorvalid && primarytextcolorvalid && secondarytextcolorvalid && brandingcolorvalid && primarybuttoncolorvalid && errorvalidationcolorvalid;
  }

  const handleReset = (name) => {
    switch(name) {
      case 'Background':
        setbackgroundcolor(savedColorConfiguration.background.value);
        setbackgroundcolorvalid(true);
        return;
      case 'Primary Text':
        setprimarytextcolor(savedColorConfiguration.primaryText.value);
        setprimarytextcolorvalid(true);
        return;
      case 'Secondary Text':
        setsecondarytextcolor(savedColorConfiguration.secondaryText.value);
        setsecondarytextcolorvalid(true);
        return;
      case 'Branding':
        setbrandingcolor(savedColorConfiguration.branding.value);
        setbrandingcolorvalid(true);
        return;
      case 'Primary Button Labels':
        setprimarybuttoncolor(savedColorConfiguration.primaryButtonLabel.value);
        setprimarybuttoncolorvalid(true);
        return;
      case 'Error Validation':
        seterrorvalidationcolor(savedColorConfiguration.errorValidation.value);
        seterrorvalidationcolorvalid(true);
        return;
    }
  }

  const controller = (
    <ConfigController
      isOpened={isOpened}
      setIsOpened={setIsOpened}
      handleCancel={handleCancel}
      handleSave={handleColorSchemeSave}
      disableSave={disableSave}
      processing={savingColorConfiguration}
      title='Color Scheme'
      description={<SectionDescription
        content={
          <div className={'colorspreview'}>
            <span className={`swatch`} style={{ background: savedColorConfiguration.background.value, right: '-15px', zIndex: 1 }} />
            <span className={`swatch`} style={{ background: savedColorConfiguration.primaryText.value, zIndex: 2 }} />
            <span className={`swatch`} style={{ background: savedColorConfiguration.secondaryText.value, left: '-15px', zIndex: 3 }} />
            <span className={`swatch`} style={{ background: savedColorConfiguration.branding.value, left: '-30px', zIndex: 4 }} />
          </div>
        }
      />}
    />
  );

  const colortorgba = (colorstring, opacity) => {
    try {
      const cl = Color(colorstring);
      return cl.alpha(opacity).hsl().string();
    } catch(err) {
      return colorstring;
    }
  }

  return <ExpandableSection
    className={`colorschemesection ${props.classes.colorschemesection}`}
    controller={controller}
    isOpened={isOpened}
    setIsOpened={setIsOpened}
    handleCancel={handleCancel}
    handleSave={handleColorSchemeSave}
    disableSave={disableSave}
    processing={savingColorConfiguration}
  >
    <div className={'colorschemeconfig'}>
      <SectionCard>
        <div className={'content'}>
          <SectionGroup>
            <SectionGroupDescription
              title='Set custom colors'
              description='Customize your color scheme if you want to add personality to the comments.'
            />
            <div className={'colorpickercolumn'}>
              <ColorPicker
                handleReset={() => handleReset('Background')}
                valid={backgroundcolorvalid}
                color={backgroundcolor}
                savedColor={savedColorConfiguration.background.value}
                label={'Background'}
                processColorUpdate={(color, e) => processColorUpdate(color, 'Background')} 
                validateColor={(isValid, e) => validateColor(isValid, 'Background')} 
              />
              <ColorPicker 
                handleReset={() => handleReset('Primary Text')}
                valid={primarytextcolorvalid}
                color={primarytextcolor}
                savedColor={savedColorConfiguration.primaryText.value}
                label={'Primary Text'}
                processColorUpdate={(color, e) => processColorUpdate(color, 'Primary Text')} 
                validateColor={(isValid, e) => validateColor(isValid, 'Primary Text')}
              />
              <ColorPicker
                handleReset={() => handleReset('Secondary Text')}
                valid={secondarytextcolorvalid}
                color={secondarytextcolor}
                savedColor={savedColorConfiguration.secondaryText.value}
                label={'Secondary Text'}
                processColorUpdate={(color, e) => processColorUpdate(color, 'Secondary Text')} 
                validateColor={(isValid, e) => validateColor(isValid, 'Secondary Text')}
              />
              <ColorPicker
                handleReset={() => handleReset('Branding')}
                valid={brandingcolorvalid}
                color={brandingcolor}
                savedColor={savedColorConfiguration.branding.value}
                label={'Branding'}
                processColorUpdate={(color, e) => processColorUpdate(color, 'Branding')} 
                validateColor={(isValid, e) => validateColor(isValid, 'Branding')}
              />
            </div>
          </SectionGroup>
          <SectionGroup>
            <SectionGroupDescription
              title='Advanced color configuration'
              description='Configure additional colors if you see any color conflict in the preview mode.'
            />
            <div className={'colorpickercolumn'}>
              <ColorPicker
                handleReset={() => handleReset('Primary Button Labels')}
                valid={primarybuttoncolorvalid}
                color={primarybuttoncolor}
                savedColor={savedColorConfiguration.primaryButtonLabel.value}
                label={'Primary Button Labels'}
                processColorUpdate={(color, e) => processColorUpdate(color, 'Primary Button Labels')} 
                validateColor={(isValid, e) => validateColor(isValid, 'Primary Button Labels')}
              />
              <ColorPicker
                handleReset={() => handleReset('Error Validation')}
                valid={errorvalidationcolorvalid}
                color={errorvalidationcolor}
                savedColor={savedColorConfiguration.errorValidation.value}
                label={'Error Validation'}
                processColorUpdate={(color, e) => processColorUpdate(color, 'Error Validation')} 
                validateColor={(isValid, e) => validateColor(isValid, 'Error Validation')}
              />
            </div>
          </SectionGroup>
        </div>
      </SectionCard>
    </div>

    <div className={'colorschemepreview'}>
      <SectionCard>
        <div className={'previewcontainer'} style={{background: backgroundcolor}}>
          <div className={'previewheader'}>
            <span style={{color: primarytextcolor}}>15 Comments</span>
            <img src={`${url.image}/avatars/avatar-colorscheme-preview-group-2x.png`} />
          </div>
          <div className={'commentfield'} style={{background: colortorgba(primarytextcolor, 0.02), border: `2px solid ${colortorgba(primarytextcolor, 0.05)}`}}>
            <span style={{color: colortorgba(secondarytextcolor, 0.5)}}>Leave a comment</span>
          </div>
          <div className={'navbarcontainer'} style={{color: secondarytextcolor}}>
            <div className={'comments'} style={{color: primarytextcolor}}>Comments</div>
            <div className={'community'}>Community</div>
            <span>Log In</span>
          </div>
          <div className={'navbarunderline'} style={{background: brandingcolor}} />
          <div className={'sortbycontainer'} style={{color: secondarytextcolor}}>
            <span>Sort By</span>
            <div className={'popular'}>
              <span>Popular</span>
              <Icon name={'angle-down'} />
            </div>
          </div>
          <div className={'commentsection'}>
            <div className={'userinfo'}>
              <img src={`${url.image}/avatars/avatar-cu-preview-oval-2x.png`} />
              <div className={'nameanddate'}>
                <div className={'name'}>
                  <span style={{color: primarytextcolor}}>Dee Chilu…</span>
                  <div className={'diamond'} style={{background: brandingcolor}}></div>
                </div>
                <span style={{color: secondarytextcolor}}>Mar 4</span>
              </div>
              <div className={'promotedbutton'} style={{background: brandingcolor}}>
                <div className={'promoted'} style={{color: primarybuttoncolor}}>
                  <span>PROMOTED</span>
                  <Comment style={{stroke: primarybuttoncolor }} />
                </div>
              </div>
            </div>
            <div className={'comment'} style={{color: primarytextcolor}}>
              One of the sweetest, emotional and best animated film of the year.
            </div>
            <div className={'reactionbar'} style={{color: secondarytextcolor}}>
              <ThumbUp style={{stroke: secondarytextcolor, marginRight: '10px' }} />
              <span>7</span>
              <ThumbDown style={{stroke: secondarytextcolor, marginLeft: '10px', marginRight: '20px' }}/>
              <Smile style={{stroke: secondarytextcolor, fill: secondarytextcolor, marginRight: '20px' }} />
              <span>Reply</span>
              <ThreeDots style={{stroke: secondarytextcolor, marginLeft: 'auto'}} />
            </div>
            <div className={'morecommentfield'} style={{background: colortorgba(primarytextcolor,  0.05)}}>
              <span style={{color: brandingcolor}}>Load more comments</span>
            </div>
          </div>
        </div>
      </SectionCard>
    </div>
  </ExpandableSection>
}

export default injectSheet(styles)(ColorSchemeSection)